import React, { useCallback, useMemo } from "react";
import { StyleSheet, View } from "react-native";
import Switch from "../../ui_atoms/Switch";
import { useTheme } from "../../hooks/use-theme";
import {
    DateScheduleType,
    ScheduleTimeType,
    ScheduleType,
    TimeMode,
} from "../../__generated__/graphql";
import TimeListComponent from "./TimeListComponent";
import VStack from "../../ui_atoms/VStack";
import { minutesAsString } from "../../utils";
import { useDurationFormatter, useI18n } from "../../hooks/i18n-hooks";
import IconButton from "../IconButton";
import Text from "../Text";
import { isNotEmptyDaySchedule } from "../../hooks/timesheet-utils";
import { useDateFormatter } from "../../hooks/date";
import Callout from "../../ui_atoms/Callout";

export type EnabledWorkingTimeSpecificType = DateScheduleType & {
    enabled?: boolean;
    disabled?: boolean;
};

export type WorkingTimeSpecificDate = {
    dates: EnabledWorkingTimeSpecificType[];
};

type DateScheduleCellProps = {
    dateSchedule: EnabledWorkingTimeSpecificType;
    onChange: (dateSchedule: EnabledWorkingTimeSpecificType) => void;
    onShowMenu?: () => void;
    disabled?: boolean;
    disabledScheduleTimeTypes?: ScheduleTimeType[];
    vacationOverflow?: number;
};

const DateScheduleCell: React.FC<DateScheduleCellProps> = ({
    dateSchedule,
    onChange,
    onShowMenu,
    disabled = false,
    disabledScheduleTimeTypes,
    vacationOverflow = 0,
}) => {
    const componentStyle = useDateScheduleCellStyle();
    const i18n = useI18n();
    const onChangeTimes = useCallback(
        (times: ScheduleType[]) => {
            onChange({
                ...dateSchedule,
                daySchedule: {
                    timeMode: TimeMode.Fixed,
                    fixedTimes: times,
                    freeDuration: null,
                    freeDurations: null,
                },
            });
        },
        [onChange, dateSchedule],
    );

    const totalTime = useMemo(() => {
        const minutes =
            dateSchedule.daySchedule?.fixedTimes?.reduce((acc, time) => {
                return acc + (time.endTime - time.startTime);
            }, 0) || 0;
        return minutesAsString(minutes);
    }, [dateSchedule.daySchedule?.fixedTimes]);

    const intlFormat = useDateFormatter();
    const isValidDayScheduleValue = useMemo(
        () => !dateSchedule.enabled || isNotEmptyDaySchedule(dateSchedule.daySchedule),
        [dateSchedule],
    );
    const containsVacations = useMemo(
        () =>
            dateSchedule.daySchedule?.fixedTimes?.some(
                (time) => time.type === ScheduleTimeType.Vacation,
            ) || false,
        [dateSchedule],
    );
    const formatDuration = useDurationFormatter();
    return (
        <View style={componentStyle.container}>
            <View style={componentStyle.header}>
                {dateSchedule.enabled !== undefined && (
                    <Switch
                        style={componentStyle.daySwitch}
                        value={dateSchedule.enabled}
                        onChange={(value) =>
                            onChange({
                                ...dateSchedule,
                                daySchedule: {
                                    timeMode: TimeMode.Fixed,
                                    fixedTimes: null,
                                    freeDuration: null,
                                    freeDurations: null,
                                    ...dateSchedule.daySchedule,
                                },
                                enabled: value,
                            })
                        }
                        disabled={disabled}
                    />
                )}
                <Text style={componentStyle.headerText} ellipsizeMode="tail" numberOfLines={1}>
                    {intlFormat(new Date(dateSchedule.date), {
                        weekday: "short",
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                    })}
                </Text>
                {dateSchedule.enabled && (
                    <Text style={componentStyle.headerBadge} numberOfLines={1}>
                        {totalTime}
                    </Text>
                )}
                {onShowMenu && (
                    <View style={componentStyle.headerButton}>
                        <IconButton
                            size={18}
                            icon="menu"
                            onPress={onShowMenu}
                            disabled={disabled}
                        />
                    </View>
                )}
            </View>
            {!isValidDayScheduleValue && (
                <Text style={componentStyle.errorText}>{i18n.t("workingTime.emptyTimes")}</Text>
            )}
            {vacationOverflow > 0 && containsVacations && (
                <VStack h={0.5}>
                    <Callout
                        mode="error"
                        markdown={i18n.t("workingTime.insufficient_vacation_info", {
                            value: formatDuration(vacationOverflow),
                        })}
                    />
                </VStack>
            )}
            {dateSchedule.enabled !== false && (
                <TimeListComponent
                    day={dateSchedule.date}
                    times={dateSchedule.daySchedule?.fixedTimes || []}
                    onChange={onChangeTimes}
                    disabled={disabled}
                    disabledScheduleTimeTypes={disabledScheduleTimeTypes}
                    vacationOverflow={vacationOverflow}
                />
            )}
        </View>
    );
};

const useDateScheduleCellStyle = () => {
    const { gridSize, fonts, colors, marginSize } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    borderBottomWidth: 5,
                    borderBottomColor: "#eee",
                },
                header: {
                    flexDirection: "row",
                    alignItems: "center",
                    paddingVertical: gridSize / 2,
                    paddingRight: gridSize / 2,
                    paddingLeft: gridSize / 4,
                },
                daySwitch: {
                    paddingRight: gridSize / 2,
                    paddingLeft: gridSize / 4,
                },
                headerText: {
                    flex: 1,
                    textTransform: "capitalize",
                    ...fonts.bodyMedium,
                    flexGrow: 2,
                },
                headerBadge: {
                    flex: 0,
                    textTransform: "capitalize",
                    ...fonts.bodySmall,
                    flexGrow: 1,
                    flexShrink: 0,
                    textAlign: "right",
                },
                headerButton: {
                    flexGrow: 0,
                    textAlign: "right",
                },
                errorText: {
                    ...fonts.bodySmall,
                    color: colors.error,
                    paddingBottom: gridSize / 4,
                    paddingHorizontal: marginSize,
                },
            }),
        [colors.error, fonts.bodyMedium, fonts.bodySmall, gridSize, marginSize],
    );
};

type WorkingTimeSpecificDatesComponentProps = {
    workingTime: WorkingTimeSpecificDate;
    onChange: (workingTime: WorkingTimeSpecificDate) => void;
    disabled?: boolean;
    disabledScheduleTimeTypes?: ScheduleTimeType[];
    vacationOverflow?: number;
};

const TimeSheetWorkingTimeSpecificDatesComponent: React.FC<
    WorkingTimeSpecificDatesComponentProps
> = ({ workingTime, onChange, disabledScheduleTimeTypes, disabled = false, vacationOverflow }) => {
    const onChangeDateSchedule = useCallback(
        (idx: number, dateSchedule: DateScheduleType) => {
            const newDates = [...workingTime.dates];
            newDates[idx] = {
                ...dateSchedule,
                ...(newDates[idx].enabled && dateSchedule.daySchedule?.fixedTimes?.length === 0
                    ? {
                          enabled: false,
                      }
                    : {}),
            };
            onChange({ dates: [...newDates] });
        },
        [onChange, workingTime],
    );

    return (
        <VStack>
            {workingTime.dates.map((dateSchedule, idx) => (
                <DateScheduleCell
                    dateSchedule={dateSchedule}
                    key={dateSchedule.date}
                    onChange={(dateSchedule) => onChangeDateSchedule(idx, dateSchedule)}
                    disabled={disabled || (dateSchedule.disabled && !dateSchedule.enabled)}
                    disabledScheduleTimeTypes={disabledScheduleTimeTypes}
                    vacationOverflow={vacationOverflow}
                />
            ))}
        </VStack>
    );
};

export default TimeSheetWorkingTimeSpecificDatesComponent;
